import { memo } from 'react';
import type { QuoteWithRelations } from '@cleanpro/common-ui';
import { QUOTE_STATUS, getStatusLabel, getStatusColor, DateUtils } from '@cleanpro/common-ui';
import { Calendar } from 'lucide-react';

interface QuoteRowProps {
  quote: QuoteWithRelations;
  onClick: (quote: QuoteWithRelations) => void;
}

const isExpired = (validUntil: string) => {
return new Date(validUntil) < new Date();
};

const QuoteTableRow = memo(({ quote, onClick }: QuoteRowProps) => {
  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onClick(quote)}
    >
    <td className="px-6 py-4 whitespace-nowrap">
    <div>
        <div className="text-sm font-medium text-gray-900">
        {quote.quoteNumber}
        </div>
        <div className="text-xs text-gray-400 mt-1">
        {quote.lineItems.length} item{quote.lineItems.length !== 1 ? 's' : ''}
        </div>
    </div>
    </td>

    <td className="px-6 py-4 whitespace-nowrap">
    <div>
        <div className="text-sm text-gray-500">
        {quote.customer?.name}
        </div>
    </div>
    </td>
    
    <td className="px-6 py-4 whitespace-nowrap">
    <div className="text-sm text-gray-900">
        <div className="flex items-center">
        <span className="font-semibold">${quote.totalAmount.toFixed(2)}</span>
        </div>
        {quote.discountAmount > 0 && (
        <div className="text-xs text-green-600">
            -${quote.discountAmount.toFixed(2)} discount
        </div>
        )}
    </div>
    </td>
    
    <td className="px-6 py-4 whitespace-nowrap">
    <div className="flex items-center space-x-2">
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-lg ${getStatusColor(quote.status, 'quote')}`}>
        {getStatusLabel(quote.status, 'quote')}
        </span>
    </div>
    {quote.viewedAt && quote.status === QUOTE_STATUS.AWAITING_RESPONSE && (
        <div className="text-xs text-gray-400 mt-1">
        Viewed {DateUtils.formatDateLong(quote.viewedAt)}
        </div>
    )}
    </td>
    
    <td className="px-6 py-4 whitespace-nowrap">
    <div className={`text-sm flex items-start ${isExpired(quote.validUntil) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
        <Calendar className="h-3 w-3 mr-1 text-gray-400 mt-0.5" />
        <span>{DateUtils.formatDateTimeWithRelative(quote.validUntil)}</span>
    </div>
    {isExpired(quote.validUntil) && (
        <div className="text-xs text-red-500">
        Expired
        </div>
    )}
    </td>

    <td className="px-6 py-4 whitespace-nowrap">
    <div className="text-sm text-gray-900 flex items-start">
        <Calendar className="h-3 w-3 mr-1 text-gray-400 mt-0.5" />
        <span>{DateUtils.formatDateTimeWithRelative(quote.createdAt)}</span>
    </div>
    </td>
</tr>
  );
});

QuoteTableRow.displayName = 'QuoteTableRow';

export default QuoteTableRow;